import { pickWeighted } from "../utils/WeightedRandom";
import { isWithinDateWindow } from "../utils/TimeUtils";
import type { HolidayCalendar } from "./HolidayCalendar";
import type { VignetteDefinition } from "./vignette-defs/types";

/**
 * Holds the vignette registry and picks what plays next: a holiday vignette
 * is near-guaranteed while its real-calendar window is active (matching the
 * original screensaver concept's easter-egg behavior); otherwise eligible
 * common/rare vignettes are weighted-randomly selected via `pickWeighted`.
 */
export class SceneDirector {
  private readonly registry: VignetteDefinition[];
  private readonly holidayCalendar: HolidayCalendar;

  constructor(registry: VignetteDefinition[], holidayCalendar: HolidayCalendar) {
    this.registry = registry;
    this.holidayCalendar = holidayCalendar;
  }

  /**
   * `timeOfDay` (0..1) comes from DayClock; `simDayCount` from LongArcState
   * (gates `minSimDay`/`maxSimDay` — unexercised until this phase, see
   * docs/Castaway Plan.md); `now` is the real Date for holiday checks
   * (overridable for testing/forcing).
   */
  pickNext(timeOfDay: number, simDayCount: number, now: Date = new Date()): VignetteDefinition | null {
    const activeHolidayVignetteId = this.holidayCalendar.getActiveHolidayVignetteId(now);
    if (activeHolidayVignetteId) {
      const holidayVignette = this.registry.find((v) => v.id === activeHolidayVignetteId);
      // Near-guaranteed, not literally 100% — matches the "easter egg" feel
      // rather than forcing the holiday vignette every single cycle.
      if (holidayVignette && Math.random() < 0.8) return holidayVignette;
    }

    const eligible = this.registry.filter((v) => this.isEligible(v, timeOfDay, simDayCount, now));
    return pickWeighted(eligible);
  }

  /** Bypasses weighting/date gating entirely — for debug overrides and force-trigger verification. */
  getById(id: string): VignetteDefinition | undefined {
    return this.registry.find((v) => v.id === id);
  }

  listIds(): string[] {
    return this.registry.map((v) => v.id);
  }

  private isEligible(vignette: VignetteDefinition, timeOfDay: number, simDayCount: number, now: Date): boolean {
    // Holiday vignettes only ever play via the guaranteed path above (or a
    // forced override) — never through ordinary weighted selection, so a
    // windowed one doesn't compete on weight against commons and a
    // windowless one (misconfigured) doesn't show up year-round.
    if (vignette.category === "holiday") return false;

    if (vignette.dateWindow && !isWithinDateWindow(now, vignette.dateWindow)) return false;

    if (vignette.timeOfDay && vignette.timeOfDay !== "any") {
      const isDaytime = timeOfDay > 0.25 && timeOfDay < 0.75;
      if (vignette.timeOfDay === "day" && !isDaytime) return false;
      if (vignette.timeOfDay === "night" && isDaytime) return false;
    }

    if (vignette.minSimDay !== undefined && simDayCount < vignette.minSimDay) return false;
    if (vignette.maxSimDay !== undefined && simDayCount > vignette.maxSimDay) return false;

    return true;
  }
}
